import * as turf from "@turf/turf";
import * as colors from "./Colors";
import {Feature} from "geojson";
import * as drf from "./DataRequestFunctions";

export function getIntersectsObjects(mapViews, lngLat) {
    return mapViews.filter(f => {
            let geometry = f.geometry;
            if (!geometry) return false;
            return turf.booleanPointInPolygon(
                turf.point([lngLat.lng, lngLat.lat]),
                turf.polygon(geometry.coordinates)
            );
        }
    );
}

const COMPETITOR_LAYER_NAMES = new Set(["конкуренты", "competitors"]);

function layerLooksLikeCompetitors(layer) {
    const names = [layer?.full_name, layer?.name, layer?.located_name, layer?.title];
    return names.some((n) => COMPETITOR_LAYER_NAMES.has(String(n ?? "").trim().toLowerCase()));
}

export function isCompetitorsLayer(products, currentProductIndex, selectedLayerId) {
    const product = products?.[currentProductIndex];
    if (!product) return false;
    const layer = product.layers?.find(l => String(l.id) === String(selectedLayerId));
    return layerLooksLikeCompetitors(layer);
}

/**
 * @param feature {Feature}
 * @param objectId {number}
 * @return {Feature}
 */
export function highlightFeature(feature, objectId) {
    const original = feature.properties.originalColor ?? feature.properties.color;
    const requiredId = feature.properties.object_id;
    const isTarget = String(requiredId) === String(objectId);
    const brighten = colors.brightenColor(original);
    const lighten = colors.lightenColor(original);
    const darken = colors.darkenColor(original);
    return {
        ...feature,
        properties: {
            ...feature.properties,
            originalColor: original,
            color: isTarget ? brighten : lighten,
            outlineColor: isTarget ? darken : "rgba(0,0,0,0.2)",
        },
    };
}

/**
 * @param layer {Layer}
 * @param objectId {number}
 * @param selectedYear {number}
 * @param month {number}
 * @return {Promise<{number: number}>}
 */
export async function getLayerData(layer, objectId, selectedYear, month) {
    const allData = {};
    (await Promise.all(layer.features.map(async (feature) => {
        try {
            const data = await drf.getDataOfPickedArea(objectId, selectedYear, month);
            return {featureId: feature.id, data};
        } catch (err) {
            console.error(`Error loading data for feature ${feature.id}:`, err);
            return {featureId: feature.id, data: null};
        }
    }))).forEach(result => {
        allData[result.featureId] = result.data?.[result.featureId] ?? null;
    });
    return allData;
}

/**
 *
 * @param objectId {number}
 * @param featureId {number}
 * @param objectData
 * @param views {Feature[]}
 * @return {Feature[]}
 */
export function labelFeatures(objectId, featureId, objectData, views) {
    if (!objectId || !featureId || !objectData) return [];
    const selectedHexFeature = views.find(f => String(f.properties.object_id) === String(objectId));
    if (!selectedHexFeature) return [];
    const value = objectData[featureId];
    if (value == null) return [];
    let polygon = turf.polygon(selectedHexFeature.geometry.coordinates);
    const center = turf.centroid(polygon);
    return [{
        type: 'Feature',
        geometry: center.geometry,
        properties: {
            object_id: objectId,
            label: String(value)
        }
    }];
}

/**
 * Finds the hex closest to the centroid of all region hexes.
 * @param {import('geojson').Feature[]} features
 * @return {{center: [number, number], objectId: *}|null}
 */
export function getCentralHexCenter(features) {
    if (!features?.length) return null;

    const valid = features.filter(f =>
        f?.geometry && (f.geometry.type === 'Polygon' || f.geometry.type === 'MultiPolygon')
    );
    if (!valid.length) return null;

    try {
        const collectionCentroid = turf.centroid(turf.featureCollection(valid));
        let closest = valid[0];
        let closestDist = Infinity;

        for (const feature of valid) {
            const hexCentroid = turf.centroid(feature);
            const dist = turf.distance(hexCentroid, collectionCentroid, { units: 'kilometers' });
            if (dist < closestDist) {
                closestDist = dist;
                closest = feature;
            }
        }

        const centerFeature = turf.centroid(closest);
        const [lon, lat] = centerFeature.geometry.coordinates;
        if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null;

        return {
            center: [lon, lat],
            objectId: closest.properties?.object_id ?? null,
        };
    } catch (e) {
        console.error('getCentralHexCenter failed', e);
        return null;
    }
}

/**
 * @param newRegion {GeoRegion}
 * @param resolveRegionOfProduct
 * @param product {Product}
 * @param hexSize {number}
 */
export function onRegionChange(newRegion, resolveRegionOfProduct, product, hexSize) {
    drf.getProductMapObjects(newRegion.id, hexSize)
        .then(featureCollection => resolveRegionOfProduct(featureCollection, product, newRegion))
        .catch(console.error);
}

export function buildHexLabels(hexagons, valuesMap) {
    if (!hexagons?.length || !valuesMap) return [];

    return hexagons.map(hex => {
        const objectId = hex.properties.object_id;
        const value = valuesMap[objectId];

        if (value == null) return null;

        const center = turf.centroid(hex);

        return {
            type: "Feature",
            geometry: center.geometry,
            properties: {
                object_id: objectId,
                label: String(value)
            }
        };
    }).filter(Boolean);
}